import {
  ActionSheetButton,
  IonActionSheet,
  IonLabel,
  IonList,
  IonListHeader,
} from '@ionic/react';
import Fuse from 'fuse.js';
import { orderBy } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Can, useHasPermission } from '../../api/authorization.provider';
import {
  FindRolesOptionsBaseRolesFilter,
  ListTeamUsersDocument,
  ListUsersDocument,
  ListUsersInput,
  ListUsersQuery,
  Maybe,
  PlanEntitlementKey,
  Role,
  TeamRole,
  TeamUser,
  User,
  useListRolesQuery,
  useListTeamUsersQuery,
  useListUsersQuery,
  useUpdateTeamAccessMutation,
} from '../../api/graphql/generated';
import { useListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { trackInvitedUser } from '../../config/analytics';
import i18n from '../../i18n';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { useActiveWorkspace } from '../../store/workspace/workspace.hooks';
import { Avatar } from '../avatar/avatar';
import { IonItem } from '../ionic/IonItem';
import '../paywall/paywall.css';
import { usePaywallActionModalLazy } from '../paywall/paywall-modal-wrapper';
import { RoleTypeToLocalizedKey } from '../role/role.types';
import { SaveStatusToast } from '../toast/toast.gql';
import { Searchbar, SearchbarRef } from '../ui/searchbar/searchbar';

interface TeamUsersProps {
  teamId: string;
}

interface MaybeTeamUser {
  customRole?: Maybe<Role>;
  user: Partial<User>;
}

interface SectionData {
  title: string;
  data: MaybeTeamUser[];
}

interface TeamUserItemProps {
  user?: Partial<User>;
  customRole?: Maybe<Role>;
  teamId: string;
  isOwnProfile?: boolean;
  canUpdateTeam: boolean;
  onClick?: (user: Partial<User>, customRole?: Maybe<Role>) => Promise<void>;
}

const emailValidation = yup.string().email();
const baseRolesOrder: Record<TeamRole, number> = {
  [TeamRole.MANAGER]: 1,
  [TeamRole.EDITOR]: 2,
  [TeamRole.OPERATOR]: 3,
  [TeamRole.VIEWER]: 4,
};

const TeamUsers = (props: TeamUsersProps): ReactElement => {
  const { teamId } = props;
  const { t } = useTranslation();
  const workspace = useActiveWorkspace();
  const [searchText, setSearchText] = useState<string>();
  const [isValidEmail, setValidEmail] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<MaybeTeamUser | null>(null);
  const searchbarRef = useRef<SearchbarRef>(null);

  const { data: listRolesData } = useListRolesQuery({
    variables: {
      input: {
        teamIds: [teamId],
        baseRolesFilter: FindRolesOptionsBaseRolesFilter.SHOW,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const roles = useMemo(() => {
    return listRolesData?.listRoles.items ?? [];
  }, [listRolesData?.listRoles.items]);

  // custom roles are prioritized and ordered by name
  // base roles are ordered by our record (Manager, Editor, Operator, Viewer)
  const orderedRoles = useMemo(() => {
    return orderBy(roles, [
      (r) => {
        if (r.isBaseRole) {
          return baseRolesOrder[r.type as TeamRole];
        } else {
          return -1;
        }
      },
      'name',
    ]);
  }, [roles]);

  const [updateTeamAccess, { loading, error }] = useUpdateTeamAccessMutation();
  const canUpdateTeam = useHasPermission('update', 'team', { id: teamId });

  const myId = workspace?.user.id;

  const { data: teamUsersData } = useListTeamUsersQuery({
    variables: { input: { id: teamId, limit: 100, offset: 0 } },
    fetchPolicy: 'cache-and-network',
  });

  const {
    infiniteScroll,
    result: { data: allUsersData },
  } = useListQueryWithInfiniteScroll<ListUsersQuery, ListUsersInput>({
    useQuery: useListUsersQuery,
    input: {
      searchTerm: searchText,
    },
    queryOptions: {
      fetchPolicy: 'cache-and-network',
    },
  });

  const teamUsers: TeamUser[] = teamUsersData?.listTeamUsers.items ?? [];
  const allUsers: User[] = allUsersData?.listUsers.items ?? [];

  const teamUserIds: string[] = teamUsers.map((teamUser) => teamUser.user.id);
  const remainingUsers = allUsers
    .filter((u) => !teamUserIds.includes(u.id))
    .map((u) => ({ user: u }));

  let filteredTeamUsers = teamUsers;

  if (searchText) {
    const fuseUsers = new Fuse(filteredTeamUsers, {
      keys: ['role', 'user.firstName', 'user.lastName', 'user.email'],
    });

    filteredTeamUsers = fuseUsers.search(searchText).map((el) => el.item);
  }

  const validateEmail = async (text?: string) => {
    if (text) {
      const isValid = await emailValidation.isValid(text);
      const isExistingUser = teamUsers.map((u) => u.user.email).includes(text);
      if (!isExistingUser) {
        setValidEmail(isValid);
      } else {
        setValidEmail(false);
      }
    } else {
      setValidEmail(false);
    }
  };

  useEffect(() => {
    validateEmail(searchText?.trim());
  });

  const updateAccess = useCallback(
    async (
      user: Partial<User>,
      newRole: TeamRole | 'revoke',
      customRoleId?: string
    ) => {
      const refetchQueries = [ListTeamUsersDocument];

      if (newRole !== 'revoke' && user?.id) {
        await updateTeamAccess({
          variables: {
            input: {
              id: teamId,
              grant: [{ role: newRole, userId: user.id, customRoleId }],
            },
          },
          refetchQueries,
        });
      } else if (newRole !== 'revoke' && user?.id === undefined) {
        await updateTeamAccess({
          variables: {
            input: {
              id: teamId,
              create: [
                {
                  role: newRole,
                  customRoleId,
                  user: { email: user?.email },
                },
              ],
            },
          },
          refetchQueries: [
            ...refetchQueries,
            {
              query: ListUsersDocument,
              variables: { input: { limit: 50, offset: 0 } },
            },
          ],
        });
        trackInvitedUser();
        searchbarRef.current?.setSearchText(undefined);
      } else if (newRole === 'revoke' && user?.id) {
        await updateTeamAccess({
          variables: {
            input: { id: teamId, revoke: [user.id] },
          },
          refetchQueries,
        });
      }
    },
    [teamId, updateTeamAccess]
  );

  const { paywallAction: paywallAction, paywallModal: modal } =
    usePaywallActionModalLazy();

  const actionButtons = useMemo(() => {
    const buttons: ActionSheetButton[] = [];

    buttons.push({
      text: t('team_role_remove_access'),
      role: 'destructive',
      handler: () => {
        if (selectedUser?.user) {
          updateAccess(selectedUser?.user, 'revoke');
        }
      },
    });

    orderedRoles.forEach((role) => {
      const isCustom = !role.isBaseRole;

      const name = isCustom
        ? getLocalizedText('common:custom_role_name', {
            roleName: role.name,
          }) ?? `${role.name} (Custom)`
        : getLocalizedText(RoleTypeToLocalizedKey[role.type as TeamRole]);

      const actionButton: ActionSheetButton = {
        text: name,
      };

      if (role.type === TeamRole.OPERATOR) {
        actionButton.handler = () => {
          paywallAction({
            planEntitlementKey: PlanEntitlementKey.TEAM_OPERATOR_ROLE,
            action: () => {
              if (selectedUser?.user) {
                updateAccess(selectedUser?.user, TeamRole.OPERATOR, role.id);
              }
            },
          });
        };
      } else {
        actionButton.handler = () => {
          if (selectedUser?.user) {
            updateAccess(selectedUser?.user, role.type, role.id);
          }
        };
      }

      if (isCustom) {
        actionButton.cssClass = 'custom-role';
      }

      buttons.push(actionButton);
    });

    buttons.push({
      text: t('cancel'),
      role: 'cancel',
    });

    return buttons;
  }, [orderedRoles, paywallAction, selectedUser?.user, t, updateAccess]);

  const onUserClick = async (user: Partial<User>, customRole?: Maybe<Role>) => {
    setSelectedUser({ user, customRole });
  };

  const sectionData: SectionData[] = [
    {
      title: t('me'),
      data: filteredTeamUsers.filter((u) => u.user.id === myId),
    },
    {
      title: t('team_members'),
      data: filteredTeamUsers.filter((u) => u.role && u.user.id !== myId),
    },
  ];

  if (canUpdateTeam) {
    sectionData.push({
      title: t('team_other_users'),
      data: remainingUsers.map((u) => ({
        user: u.user,
      })),
    });
  }

  return (
    <>
      <Searchbar
        ref={searchbarRef}
        ionSearchbarProps={{
          autocomplete: 'email',
          placeholder: t('team_search_for_user'),
          type: 'email',
        }}
        onSearch={setSearchText}
      />
      {isValidEmail && (
        <Can I="update" a="team">
          <IonList>
            <IonListHeader lines="full">
              <IonLabel>{t('team_add_user')}</IonLabel>
            </IonListHeader>
            <TeamUserItem
              user={{ email: searchText }}
              teamId={teamId}
              canUpdateTeam={canUpdateTeam}
              onClick={onUserClick}
            />
          </IonList>
        </Can>
      )}

      {sectionData.map((section) => {
        if (section.data.length > 0) {
          return (
            <div key={section.title}>
              <IonListHeader lines="full">
                <IonLabel>{section.title}</IonLabel>
              </IonListHeader>
              {section.data.map(({ customRole, user }) => {
                return (
                  <TeamUserItem
                    key={user.id}
                    user={user}
                    customRole={customRole}
                    teamId={teamId}
                    isOwnProfile={user.id === myId}
                    canUpdateTeam={canUpdateTeam}
                    onClick={onUserClick}
                  />
                );
              })}
            </div>
          );
        } else {
          return null;
        }
      })}
      <Can I="update" a="team">
        <IonActionSheet
          isOpen={selectedUser ? true : false}
          header={`${t('team_access_for')} ${getNameForUser(
            selectedUser?.user
          )}`}
          onDidDismiss={() => setSelectedUser(null)}
          buttons={getOptionsForRole(actionButtons, selectedUser?.customRole)}
        />
      </Can>
      <SaveStatusToast loading={loading} error={error} />
      {modal}
      {infiniteScroll}
    </>
  );
};

const TeamUserItem = (props: TeamUserItemProps): ReactElement => {
  const { canUpdateTeam, user, customRole, isOwnProfile } = props;

  const isAdmin = useHasPermission('admin', 'tenant');
  const detail = canUpdateTeam && (!isOwnProfile || isAdmin);

  const onClick = () => {
    if (props.onClick && user && (!isOwnProfile || isAdmin)) {
      props.onClick(user, customRole);
    }
  };

  return (
    <IonItem
      lines="full"
      onClick={onClick}
      detail={detail}
      className="team-user"
    >
      <Avatar
        avatarProps={{ slot: 'start' }}
        attachment={user?.avatarAttachment}
      />
      <IonLabel>
        <h2>{getNameForUser(user)}</h2>
        <p>{user?.email}</p>
      </IonLabel>
      <IonLabel slot={'end'}>
        <p>{customRole?.name}</p>
      </IonLabel>
    </IonItem>
  );
};

const getNameForUser = (user?: Partial<User>): string | undefined => {
  let name = user?.email;
  if (user?.firstName && user?.lastName) {
    name = `${user.firstName} ${user.lastName}`;
  }
  return `${name}`;
};

const getOptionsForRole = (
  allButtons: ActionSheetButton[],
  customRole?: Maybe<Role>
): ActionSheetButton[] => {
  const buttons: ActionSheetButton[] = allButtons.filter((button) => {
    if (
      button.text === i18n.t('common:team_role_remove_access') &&
      customRole === undefined
    ) {
      return false;
    } else {
      return true;
    }
  });
  return buttons;
};

export { TeamUsers, TeamUserItem, getNameForUser };
